/* eslint-disable redos/no-vulnerable */
import React from 'react';
import ReactMarkdown, { Components, defaultUrlTransform } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Citation, CitationType } from '../../api/claims/interface';
import { EscCitation } from '../Markdown/Citation';
import CitationGroup from '../Markdown/CitationGroup';
import { extractCitationGroups } from '../Markdown/citationGroups';
import styles from './WikiMarkdown.module.scss';

// react-markdown's default sanitizer drops custom-scheme hrefs; keep the
// projector-stamped task:/claim: links (escalation: on legacy dossiers) so the
// entity chips can render.
function wikiUrlTransform(url: string): string {
  return /^(task|escalation|claim):/.test(url) ? url : defaultUrlTransform(url);
}

export function resolveWikiHref(basePath: string, href: string): string {
  const hashIdx = href.indexOf('#');
  const frag = hashIdx >= 0 ? href.slice(hashIdx) : '';
  const rel = (hashIdx >= 0 ? href.slice(0, hashIdx) : href).trim();
  if (rel.startsWith('/')) {
    return rel.replace(/^\/+/, '') + frag;
  }
  if (rel.startsWith('knowledge/')) {
    return rel + frag;
  }
  const baseDir = basePath.includes('/')
    ? basePath.slice(0, basePath.lastIndexOf('/'))
    : '';
  const stack = baseDir ? baseDir.split('/') : [];
  for (const seg of rel.split('/')) {
    if (!seg || seg === '.') {
      continue;
    }
    if (seg === '..') {
      stack.pop();
    } else {
      stack.push(seg);
    }
  }
  return stack.join('/') + frag;
}

export function headingSlug(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-');
}

function nodeText(children: React.ReactNode): string {
  return React.Children.toArray(children)
    .map((c) => (typeof c === 'string' || typeof c === 'number' ? `${c}` : ''))
    .join('');
}

interface WikiMarkdownProps {
  markdown: string;
  basePath: string;
  onCitationClick(citation: Citation): void;
  onPageLink(path: string): void;
  onTaskClick(taskId: string): void;
  onClaimClick(claimId: string): void;
}

const WikiMarkdown: React.FC<WikiMarkdownProps> = ({
  markdown,
  basePath,
  onCitationClick,
  onPageLink,
  onTaskClick,
  onClaimClick,
}) => {
  const { text, groups: citationGroups } = React.useMemo(
    () => extractCitationGroups(markdown),
    [markdown]
  );

  const components: Components = React.useMemo(
    () => ({
      h2: ({ children }) => (
        <h2 id={headingSlug(nodeText(children))}>{children}</h2>
      ),
      h3: ({ children }) => (
        <h3 id={headingSlug(nodeText(children))}>{children}</h3>
      ),
      a: ({ children, href, ...props }) => {
        if (href) {
          const citationMatch = href.match(/^citation-(\d+)$/);
          if (citationMatch) {
            const group = citationGroups[citationMatch[1]];
            if (group?.length) {
              return (
                <CitationGroup key={citationMatch[1]}>
                  {group.map((citation, i) => (
                    <EscCitation
                      key={`${citationMatch[1]}-${i}`}
                      citationIndex={`${citation.citationNumber}`}
                      citation={{
                        ...citation,
                        citation_id: href,
                        fragment_type: citation.type as CitationType,
                        pages: [citation.start_page, citation.end_page],
                      }}
                      customCitationHandler={onCitationClick}
                      hidePage
                      stateDb
                    />
                  ))}
                </CitationGroup>
              );
            }
          }
          // Managed-entity references stamped by the projector: open the same
          // drawer as elsewhere in the app. task: is current; escalation: is the
          // legacy token still present on older dossiers' wikis.
          const task = href.match(/^(?:task|escalation):([0-9a-fA-F-]{36})$/);
          if (task) {
            return (
              <a
                href={href}
                className={styles.entityRef}
                onClick={(e) => {
                  e.preventDefault();
                  onTaskClick(task[1]);
                }}
              >
                {children}
              </a>
            );
          }
          const claim = href.match(/^claim:([0-9a-fA-F-]{36})$/);
          if (claim) {
            return (
              <a
                href={href}
                className={styles.entityRef}
                onClick={(e) => {
                  e.preventDefault();
                  onClaimClick(claim[1]);
                }}
              >
                {children}
              </a>
            );
          }
          // Internal wiki/knowledge link: a .md target, optionally with a
          // #section fragment. Navigate in-app instead of leaving the page.
          if (/\.md(#.+)?$/.test(href)) {
            return (
              <a
                href={href}
                onClick={(e) => {
                  e.preventDefault();
                  onPageLink(resolveWikiHref(basePath, href));
                }}
                {...props}
              >
                {children}
              </a>
            );
          }
        }
        return (
          <a href={href} target="_blank" rel="noreferrer" {...props}>
            {children}
          </a>
        );
      },
    }),
    [
      basePath,
      citationGroups,
      onCitationClick,
      onPageLink,
      onTaskClick,
      onClaimClick,
    ]
  );

  return (
    <div className={styles.markdown}>
      <ReactMarkdown
        components={components}
        remarkPlugins={[remarkGfm]}
        urlTransform={wikiUrlTransform}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
};

export default WikiMarkdown;
